import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'gradient' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading,
  icon,
  className = '',
  ...props
}) => {
  const baseStyle = `
    inline-flex items-center justify-center font-medium transition-all rounded-lg
    focus:outline-none focus:ring-2 focus:ring-offset-1
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variants = {
    primary: `
      bg-primary-600 text-white
      hover:bg-primary-700
      focus:ring-primary-500
      dark:bg-primary-600 dark:hover:bg-primary-500
    `,
    secondary: `
      bg-surface text-theme border border-theme
      hover:bg-surface-secondary
      focus:ring-theme
      dark:bg-surface dark:border-border dark:text-theme
    `,
    danger: `
      bg-red-50 text-red-600 border border-red-100
      hover:bg-red-100
      focus:ring-red-500
      dark:bg-red-900/30 dark:text-red-400 dark:border-red-800
    `,
    ghost: `
      bg-transparent text-primary-600
      hover:text-primary-700 hover:bg-primary-50
      dark:text-primary-400 dark:hover:bg-primary-900/30
    `,
    gradient: `
      gradient-primary text-white
      hover:opacity-90 hover:shadow-lg
      focus:ring-primary-500
      shadow-md
    `,
    glass: `
      glass glass-border text-theme
      hover:bg-surface-secondary
      focus:ring-primary-500
    `,
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-6 py-3 text-base gap-2",
  };

  return (
    <button
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? (
        <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
      ) : icon ? (
        <span>{icon}</span>
      ) : null}
      {children}
    </button>
  );
};
